import { useStore } from '@nanostores/react'
import { automationApi, Tag } from 'api/automationApi'
import clsx from 'clsx'
import {
	$confirmStore,
	ConfirmDialogMode
} from 'features/messaging/ui/ConfirmDialog'
import { type MouseEvent, useId, useRef } from 'react'
import { appDay } from 'shared/day'
import { useAppDispatch } from 'shared/hooks'
import { contextActions } from 'shared/styles'
import { StackIcon } from 'shared/ui'

import type { ScheduledMessage } from '../../types'

import { api } from '../../api'
import { $rescheduleInEdit } from '../../store'
import { $timePickerState } from '../TimePicker/TimePicker'
import css from './Messages.module.css'
import { $active, openFromTrigger, useSyncPopoverState } from './utils'

Menu.open = (event: MouseEvent<HTMLButtonElement>, message: ScheduledMessage) =>
	openFromTrigger(event, message)

Menu.optionalClose = (id: string) => {
	if ($active.get()?.id === id) {
		$active.set(null)
	}
}
export function Menu() {
	const message = useStore($active)
	const noData = message === null
	const id = useId()
	const dispatch = useAppDispatch()

	const ref = useRef<HTMLDivElement | null>(null)
	useSyncPopoverState(ref)

	const [sendNow, sendNowResult] = api.useSendScheduledMessageMutation()
	const [unSchedule, unScheduleProps] = api.useUnScheduleMessageMutation()

	const isDeleting =
		unScheduleProps.isLoading && unScheduleProps.originalArgs === message?.id
	const isSending =
		sendNowResult.isLoading && sendNowResult.originalArgs === message?.id

	const actionProps = {
		className: clsx(contextActions.action, css.action),
		type: 'button' as const
	}

	return (
		<div
			className={clsx(contextActions.menu, css.menu)}
			id={id}
			// @ts-ignore Recheck in React 19
			popover='auto'
			ref={ref}
		>
			<button
				disabled={noData}
				onClick={() => {
					$rescheduleInEdit.set(message)
					ref.current?.hidePopover()
				}}
				{...actionProps}
			>
				<StackIcon icon='pencil' width={18} />
				Edit
			</button>

			<button
				disabled={noData || isSending}
				onClick={async () => {
					await sendNow(message?.id ?? '')
					ref.current?.hidePopover()
					const refetchMessagesActions = automationApi.util.invalidateTags([
						Tag.Messages
					])
					dispatch(refetchMessagesActions)
				}}
				{...actionProps}
			>
				<StackIcon className={css.send} icon='send' width={18} />
				Send now
			</button>

			<button
				disabled={noData}
				onClick={() => {
					$timePickerState.set({
						id: message?.id ?? '',
						text: message?.text ?? '',
						time: appDay(message?.time)
					})

					ref.current?.hidePopover()
				}}
				{...actionProps}
			>
				<StackIcon icon='clock' width={18} />
				Reschedule
			</button>

			<hr className={css.divider} />

			<button
				disabled={noData || isDeleting}
				onClick={() => {
					$confirmStore.set({
						confirm: () => unSchedule(message?.id ?? ''),
						mode: ConfirmDialogMode.DeleteScheduleMessage
					})

					ref.current?.hidePopover()
				}}
				{...actionProps}
			>
				<StackIcon icon='trash' width={18} />
				Delete
			</button>
		</div>
	)
}
